import React, { useMemo } from 'react';
import ContainerSectionTitle from 'components/content/shared/other/ContainerSectionTitle';
import { Column } from 'react-table';
import SimpleDataTable from 'components/content/shared/tables/SimpleDataTable';
import { ITablePropertyExtensions } from 'types/components/react_table/IReactTablePropertyExtensions';
import ILookupModel from 'types/backend_models/lookup_status/ILookupModel';
import { formatLookupModal } from 'functions/formatters';
import { formatToEuro } from 'functions/formatters/currency';
import IOrderLine from 'types/backend_models/orders/IOrderline';
import NoDataMessageContainer from 'components/content/shared/other/NoDataMessageContainer';
import { cnTblFootRow, cnTblFootCell, cnTblFootCellDense } from 'components/content/shared/tables/TableStyleConstants';
import { tooltipQty, tooltipUoM } from 'components/content/shared/Tooltips';
import { nameofFactory } from 'functions/misc/NameOf';

interface IProps
{
	orderLines: IOrderLine[],
	unitsOfMeasurement: ILookupModel[]
}

function OrderDetailLinesContainer(props: IProps)
{
	const columns = useMemo(() =>
	{
		return createColumns(props.unitsOfMeasurement);
	}, [props.unitsOfMeasurement]);
	const totalPrice = useMemo(() => getTotalPrice(props.orderLines), [props]);

	const containerTitle = 'Order Lines';

	if (props.orderLines.length === 0) {
		return (
			<div>
				<ContainerSectionTitle>{containerTitle}</ContainerSectionTitle>
				<div className="h-200">
					<NoDataMessageContainer containerClassName="h100p" message="Order has no orderlines." />
				</div>
			</div>
		)
	}

	return (
		<div>
			<ContainerSectionTitle>{containerTitle}</ContainerSectionTitle>
			<div style={{ overflowX: 'auto' }}>
				<SimpleDataTable
					columns={columns}
					data={props.orderLines}
					propertyExtensions={TablepropsExtensions}
					onRowClick={()=>null}
					footer={
						<tr className={cnTblFootRow} style={{ textAlign: 'right' }}>
							<td className={`${cnTblFootCell} ${cnTblFootCellDense}`} colSpan={columns.length}>
								<span style={{ marginRight: 35, fontWeight: 500 }}>Order total</span>{formatToEuro(totalPrice)}
							</td>
						</tr>
					}
				/>
			</div>
		</div>
	);
};

function getTotalPrice(orderLines: IOrderLine[]): number
{
	let total = 0;
	orderLines.forEach(ol => total += ol.totalPrice);
	return total;
}

const orderlineNameOf = nameofFactory<IOrderLine>();

function createColumns(unitsOfMeasure: ILookupModel[]): Column<Object>[]
{
	// Column definitions for orders table
	const columns: Column<Object>[] = [

		{
			Header: "KEY REF",
			accessor: orderlineNameOf("keyRef") as keyof Object,
			maxWidth: undefined,
			width: 30
		},
		{
			Header: "Customer REF",
			accessor: orderlineNameOf('customerRef') as keyof Object,
			minWidth: undefined,
			maxWidth: undefined,
		},
		{
			Header: "Customer Description",
			accessor: orderlineNameOf('customerDescription') as keyof Object,
			minWidth: undefined,
			maxWidth: undefined,
		},
		{
			Header: "ISA REF",
			accessor: orderlineNameOf("isaRef") as keyof Object,
			maxWidth: undefined,
			width: 30,
			Cell: (props: { cell: { value: any; }; }) => <i style={{fontSize: 12, color: '#484848'}}>{props.cell.value}</i>
		},
		{
			Header: tooltipQty(),
			accessor: orderlineNameOf('qty') as keyof Object,
			maxWidth: undefined,
			width: 25
		},
		{
			Header: tooltipUoM(),
			accessor: orderlineNameOf('unitOfMeasureId') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: any; }; }) => formatLookupModal(unitsOfMeasure, Number(props.cell.value))
		},
		{
			Header: 'Unit Price',
			accessor: orderlineNameOf('unitPrice') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: any; }; }) => formatToEuro(Number(props.cell.value))
		},
		{
			Header: 'Discount',
			accessor: orderlineNameOf('discount') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: number; }; }) => ((props.cell.value as number) * 100) + ' %'
		},
		{
			Header: 'Total Price',
			accessor: orderlineNameOf('totalPrice') as keyof Object,
			maxWidth: undefined,
			width: 30,
			Cell: (props: { cell: { value: any; }; }) => formatToEuro(Number(props.cell.value))
		}
	];

	return columns;
}

/** Used to apply additional column styling */
const TablepropsExtensions: ITablePropertyExtensions = {
	columnExtensions: [
		{ accessor: 'qty', style: { textAlign: 'right' } },
		{ accessor: 'unitOfMeasureId', style: { textAlign: 'right' } },
		{ accessor: 'unitPrice', style: { textAlign: 'right' } },
		{ accessor: 'discount', style: { textAlign: 'right' } },
		{ accessor: 'totalPrice', style: { textAlign: 'right' } }
	]
}

export default OrderDetailLinesContainer;